import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import * as serviceWorker from './serviceWorker';

let rootInstance = null;
let isInitializing = false;

// Ensure DOM is ready before rendering
function initApp() {
  // Prevent multiple simultaneous initializations
  if (isInitializing) {
    return;
  }
  isInitializing = true;
  
  const container = document.getElementById('root');
  if (!container) {
    console.error('Root container not found');
    isInitializing = false;
    return;
  }

  // Wait a bit for extensions to settle before initializing
  // This helps prevent conflicts on page refresh
  setTimeout(() => {
    try {
      // Check if React root already exists (from extension or previous render)
      if (container._reactRootContainer) {
        console.warn('React root already exists, clearing and recreating...');
        try {
          // Try to clean up existing root
          if (rootInstance) {
            rootInstance.unmount();
          }
        } catch (e) {
          // Ignore cleanup errors
        }
        container.innerHTML = '';
        rootInstance = null;
      }

      // Clear any existing content
      container.innerHTML = '';
      
      function renderApp() {
        try {
          // Only unmount if we have a valid instance
          if (rootInstance) {
            try {
              rootInstance.unmount();
            } catch (e) {
              // Ignore unmount errors (node may already be removed by extension)
              console.warn('Unmount error (ignored):', e.message);
            }
            rootInstance = null;
          }
          
          // Clear container before creating new root
          // Use try-catch to handle cases where extension has already modified DOM
          try {
            container.innerHTML = '';
          } catch (e) {
            // If innerHTML fails, try removing children one by one
            while (container.firstChild) {
              try {
                container.removeChild(container.firstChild);
              } catch (e2) {
                // Extension may have already removed it, break
                break;
              }
            }
          }
          
          rootInstance = createRoot(container);
          rootInstance.render(<App />);
          console.log('React app rendered successfully');
        } catch (error) {
          console.error('Error rendering React app:', error);
          // If createRoot fails because root already exists
          if (error.message && error.message.includes('already been passed to createRoot')) {
            console.warn('Root already exists, clearing and retrying...');
            try {
              container.innerHTML = '';
            } catch (e) {
              // Ignore
            }
            // Wait a bit and retry
            setTimeout(() => {
              try {
                rootInstance = createRoot(container);
                rootInstance.render(<App />);
              } catch (e) {
                console.error('Retry failed:', e);
                // Last resort: show error message
                container.innerHTML = '<div style="padding: 20px; text-align: center; color: #fff; background: #0f0f0f;">Error loading app. Please disable browser extensions and refresh.</div>';
              }
            }, 100);
          } else {
            // For other errors, show a user-friendly message
            container.innerHTML = '<div style="padding: 20px; text-align: center; color: #fff; background: #0f0f0f;">Error loading app. Please refresh the page.</div>';
          }
        }
      }

      console.log('Initializing React app...');
      renderApp();
      
      // Simplified monitoring - only check periodically, don't use MutationObserver
      // MutationObserver was causing conflicts with extensions
      setInterval(() => {
        const textContent = container.textContent || '';
        // Only re-render if we detect unwanted content AND React content is missing
        if (textContent.includes('Practice') && !textContent.includes('Kevin')) {
          console.warn('Detected unwanted content, re-rendering React...');
          renderApp();
        }
      }, 2000); // Check every 2 seconds instead of on every mutation
      
      // Re-render on window focus
      window.addEventListener('focus', () => {
        const textContent = container.textContent || '';
        if (textContent.includes('Practice') && !textContent.includes('Kevin')) {
          renderApp();
        }
      });
      
      isInitializing = false;
    } catch (error) {
      console.error('Error initializing React app:', error);
      container.innerHTML = '<div style="padding: 20px; text-align: center; color: red;">Error loading application. Please check the console for details.</div>';
      isInitializing = false;
    }
  }, 100); // Small delay to let extensions initialize first
}

// Wait for DOM and scripts to be ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  // DOM is already ready
  initApp();
}

// Unregister service worker to avoid caching issues in development
serviceWorker.unregister();

// Global error handler to catch and suppress removeChild errors from extension interference
window.addEventListener('error', (event) => {
  if (event.error && event.error.message && event.error.message.includes('removeChild')) {
    console.warn('Suppressed removeChild error from extension interference:', event.error.message);
    event.preventDefault(); // Prevent default error handling
    event.stopPropagation(); // Stop error from bubbling
    
    // Re-render React to recover
    const container = document.getElementById('root');
    if (container && rootInstance) {
      setTimeout(() => {
        try {
          const textContent = container.textContent || '';
          if (textContent.includes('Practice') || !textContent.includes('Kevin')) {
            console.log('Re-rendering after removeChild error...');
            if (rootInstance) {
              try {
                rootInstance.unmount();
              } catch (e) {
                // Ignore
              }
            }
            container.innerHTML = '';
            rootInstance = createRoot(container);
            rootInstance.render(<App />);
          }
        } catch (e) {
          console.error('Failed to recover:', e);
        }
      }, 100);
    }
    return false;
  }
}, true); // Use capture phase to catch early

// Also catch unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message && event.reason.message.includes('removeChild')) {
    console.warn('Suppressed removeChild promise rejection from extension interference');
    event.preventDefault();
    return false;
  }
});
